import React, { Fragment, useCallback, useMemo, useState } from "react";
import { Button, Icon, ModalSelectVariation } from "@components";
import { NavigationPathsEnum, VariationTypeEnum } from "@utilities/enums";
import { formatPrice, formatTextMaxChars, getAvailableStock, getProductSellingPrice } from "@utilities/functions";
import { useNavigation } from "@utilities/hooks";
import { AddProductInBasketType, Basket, BasketItem } from "@utilities/types";

type SelectedVariation = {
  selectedColor: string | null;
  selectedSize: string | null;
}

interface Props {
  basket: Basket | null;
  totalSavings?: number; // Économie totale grâce aux promotions
  onAddProductInBasket: (basketElement: Partial<AddProductInBasketType>) => void
  onRemoveProductInBasket: (basketElement: AddProductInBasketType, isRemoveFullElement?: boolean) => void
  onStacheBasket: () => void;
}

export const BasketComponent: React.FC<Props> = ({
  basket,
  totalSavings = 0,
  onAddProductInBasket,
  onRemoveProductInBasket,
  onStacheBasket
}) => {

  const { navigateTo } = useNavigation();
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedProductItem, setSelectedProductItem] = useState<BasketItem | null>(null);

  const basketItems = useMemo(() => basket?.basket || [], [basket]);

  const handleConfirm = useCallback(() => navigateTo(NavigationPathsEnum.PAYMENT), [navigateTo]);

  const handleAddProduct = useCallback((
    basketItem: BasketItem,
    selectedVariations?: SelectedVariation,
  ) => {
    if (basketItem.product.variationType === VariationTypeEnum.BOTH && selectedVariations) {
      const findBasketItem = basketItems.find((item) => item.selectedColor === selectedVariations.selectedColor && item.selectedSize === selectedVariations.selectedSize);
      if (findBasketItem) {
        basketItem = findBasketItem;
      } else {
        basketItem.selectedColor = selectedVariations.selectedColor!;
        basketItem.selectedSize = selectedVariations.selectedSize!;
      }
    }
    if (basketItem.product.variationType === VariationTypeEnum.COLOR) {
      const findBasketItem = basketItems.find((item) => item.selectedColor === selectedVariations?.selectedColor);
      if (findBasketItem) {
        basketItem = findBasketItem;
      } else {
        basketItem.selectedColor = selectedVariations?.selectedColor!;
      }
    }
    if (basketItem.product.variationType === VariationTypeEnum.SIZE) {
      const findBasketItem = basketItems.find((item) => item.selectedSize === selectedVariations?.selectedSize);
      if (findBasketItem) {
        basketItem = findBasketItem;
      } else {
        basketItem.selectedSize = selectedVariations?.selectedSize!;
      }
    }

    onAddProductInBasket({
      basketID: basket?._id || undefined,
      productItem: basketItem,
    });
  }, [basket, basketItems, onAddProductInBasket]);

  const handleUpdateProductQuantity = useCallback((item: BasketItem, newQuantity: number) => {
    const totalAvailable = getAvailableStock(item.product, item.selectedColor, item.selectedSize);

    if (newQuantity > totalAvailable) {
      alert("Quantité dépassée. Stock insuffisant.");
      return;
    }

    const updatedItem = { ...item, quantity: newQuantity };
    onAddProductInBasket({
      basketID: basket?._id || undefined,
      productItem: updatedItem,
      replaceQuantity: true
    });
  }, [basket, onAddProductInBasket]);

  const handleRemoveProduct = useCallback((
    basketItem: BasketItem,
    isRemoveFullElement = false,
  ) => {
    if (!basket) return;

    onRemoveProductInBasket({
      basketID: basket._id,
      productItem: basketItem,
    }, isRemoveFullElement);
  }, [basket, onRemoveProductInBasket]);

  return (
    <Fragment>
      <div className="space-y-0 max-h-64 overflow-y-auto">
        {
          basketItems?.length === 0 ? (
            <p className="text-sm text-center text-gray-500 py-6">Le panier est vide.</p>
          ) : (
            basketItems?.map((item, index) => {

              const productImg = item.product?.images?.[0];
              const baseURl = process.env.REACT_APP_IMAGE_BASE_URL;
              const imgURl = `${baseURl}/${productImg?.fileDir}/${productImg?.filename}`;

              return (
                <div key={item?.product?._id + String(index) || index} className="relative py-4">
                  <div className="flex items-center space-x-2 sm:space-x-4">

                    <img
                      src={imgURl}
                      alt={item?.product?.name}
                      className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-lg"
                    />

                    <div className="flex-1">
                      <div className="flex flex-col">
                        <span className="text-sm sm:text-base">
                          {item?.product?.name ? formatTextMaxChars(item.product.name, 15) : "Produit"}
                        </span>

                        <div className="flex items-center mt-1 space-x-2">
                          {
                            item?.selectedSize && (
                              <span className="text-xs sm:text-sm bg-gray-200 rounded-full px-2 py-1">
                                {item?.selectedSize}
                              </span>
                            )
                          }
                          {
                            item?.selectedColor && (
                              <span
                                className="w-4 h-4 rounded-full border"
                                style={{
                                  backgroundColor: item?.selectedColor,
                                  borderColor: "#000",
                                }}
                              />
                            )
                          }
                        </div>

                        {/* Prix avec promo si applicable */}
                        {item?.discountedPrice && item.discountedPrice > 0 ? (
                          <div className="flex flex-col mt-1">
                            <span className="text-xs text-gray-400 line-through">
                              {formatPrice(item.price * item.quantity)}
                            </span>
                            <span className="text-xs sm:text-sm font-semibold text-red-500">
                              {formatPrice(item.discountedPrice * item.quantity)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs sm:text-sm font-semibold mt-1">
                            {item?.product ? formatPrice(getProductSellingPrice(item.product) * item.quantity) : "Prix indisponible"}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-row items-center bg-primary px-0.5 py-0.5 rounded-full space-x-1 h-7 sm:h-10 mt-2 w-fit">
                        <Button
                          className="h-6 w-6 sm:h-9 sm:w-9 min-h-0 p-0 btn-circle btn-secondary text-xs sm:text-sm font-bold"
                          onClick={() => handleRemoveProduct(item)}
                        >-</Button>

                        <input
                          type="number"
                          className="w-10 bg-white sm:w-14 text-xs sm:text-sm font-semibold text-center rounded"
                          min={1}
                          value={item.quantity}
                          onChange={(e) => {
                            const newQuantity = parseInt(e.target.value, 10);
                            if (!isNaN(newQuantity) && newQuantity >= 1) {
                              handleUpdateProductQuantity(item, newQuantity);
                            }
                          }}
                        />

                        <Button
                          className="h-6 w-6 sm:h-9 sm:w-9 min-h-0 p-0 btn-circle btn-secondary text-xs sm:text-sm font-bold"
                          onClick={() => {
                            if (item.selectedColor || item.selectedSize) {
                              setSelectedProductItem(item);
                              setModalOpen(true);
                            } else {
                              handleAddProduct(item);
                            }
                          }}
                        >+</Button>
                      </div>
                    </div>

                    {/* Supprimer */}
                    <button
                      className="absolute bottom-4 right-0 p-1.5 hover:bg-red-100 rounded-full transition-colors"
                      aria-label="Supprimer le panier"
                      onClick={() => handleRemoveProduct(item, true)}
                    >
                      <Icon iconName="Trash2" size={20} />
                    </button>
                  </div>

                  {index < basketItems?.length - 1 && (<div className="h-px bg-gray-200 mt-4" />)}
                </div>
              )
            })
          )
        }
      </div>

      {
        basketItems?.length > 0 && (
          <div className="flex flex-col mt-4">
            {/* Économie si applicable */}
            {totalSavings > 0 && (
              <div className="flex justify-between items-center text-sm text-green-600 mb-2 bg-green-50 rounded px-2 py-1">
                <span className="flex items-center gap-1">
                  <Icon iconName="Tag" size={14} />
                  Économie campagne
                </span>
                <span className="font-medium">-{formatPrice(totalSavings)}</span>
              </div>
            )}
            <div className="flex justify-between items-center font-semibold">
              <span>Total:</span>
              <span>{formatPrice(basket?.totalAmount!)}</span>
            </div>
            <button
              onClick={handleConfirm}
              className="btn btn-secondary w-full mt-4"
            >
              Passer à la caisse
            </button>
            <button
              onClick={onStacheBasket}
              className="btn btn-warning w-full mt-4"
            >
              Mettre le panier de côté
            </button>
          </div>
        )
      }

      {
        selectedProductItem && (
          <ModalSelectVariation
            isOpen={isModalOpen}
            onClose={() => setModalOpen(false)}
            product={selectedProductItem.product}
            onValidate={(selectedColor, selectedSize) => {
              handleAddProduct(selectedProductItem, {
                selectedColor, selectedSize,
              });
              setModalOpen(false);
            }}
          />
        )
      }
    </Fragment>
  );
};
